"use client";

import { IconCreditCard, IconRefresh, IconShield, IconUser } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { ClientError, gql } from "graphql-request";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useState } from "react";
import { isOnboardingRole, ONBOARDING_ROLES } from "@/components/onboarding/onboarding.roles";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/components/ui/snackbar";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAuth, useSession } from "@/lib/auth";
import { requestChangePasswordEmail } from "@/lib/auth/change-password";
import { useBillingOverview } from "@/lib/billing/useBillingOverview";
import { useReconcileBilling } from "@/lib/billing/useReconcileBilling";
import { shouldSkipErrorToast } from "@/lib/errors";
import { graphqlErrorMessage, graphqlRequest } from "@/lib/graphql/client";
import { useGraphqlQuery } from "@/lib/graphql/useGraphql";

type ProfileStringField = "first_name" | "last_name" | "jobtitle" | "company";

type UpdateProfileResult = {
  updateUser: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    jobtitle: string | null;
    company: string | null;
    name: string | null;
  };
};

/** Build a mutation that only includes fields being updated (omitted args stay local-only). */
const buildUpdateProfileDocument = (fields: ProfileStringField[]) => {
  const variableDefs = ["$id: ID!", ...fields.map((field) => `$${field}: String`)].join(", ");
  const args = ["id: $id", ...fields.map((field) => `${field}: $${field}`)].join(", ");

  return gql`
    mutation UpdateAccountProfile(${variableDefs}) {
      updateUser(${args}) {
        id
        first_name
        last_name
        jobtitle
        company
        name
      }
    }
  `;
};

const normalizeOptionalString = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
};

const accountAuthMethodsDocument = gql`
  query AccountAuthMethods {
    me {
      id
      auth_methods {
        can_change_password
        oauth_providers
      }
    }
  }
`;

const readOnlyFieldClassName =
  "bg-muted/40 pointer-events-none cursor-default focus-visible:border-input focus-visible:ring-0";

const displayName = (first: string | null | undefined, last: string | null | undefined, fallback: string) => {
  const joined = [first, last].filter(Boolean).join(" ").trim();
  return joined || fallback;
};

const formatOauthProviderLabel = (provider: string): string => {
  const normalized = provider.toLowerCase();
  if (normalized.includes("google")) return "Google";
  if (normalized.includes("github")) return "GitHub";
  return provider.replace(/OAuth$/i, "");
};

export default function AccountPage() {
  const { data: session, isPending } = useSession();
  const { user: authUser } = useAuth();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: billingData } = useBillingOverview();
  const { mutate: reconcile, isPending: isReconciling } = useReconcileBilling();

  const user = authUser ?? session?.user ?? null;
  const needsReconciliation = billingData?.billing_overview.needs_reconciliation ?? false;

  const { data: authMethodsData } = useGraphqlQuery<{
    me: {
      id: string;
      auth_methods: {
        can_change_password: boolean;
        oauth_providers: string[];
      };
    };
  }>({
    queryKey: ["accountAuthMethods"],
    document: accountAuthMethodsDocument,
    enabled: Boolean(user),
  });

  const canChangePassword = authMethodsData?.me.auth_methods.can_change_password === true;
  const oauthProviders = authMethodsData?.me.auth_methods.oauth_providers ?? [];
  const oauthPasswordTooltip = useMemo(() => {
    const labels = oauthProviders.map(formatOauthProviderLabel);
    if (labels.length === 0) return "Password is managed by your sign-in provider.";
    if (labels.length === 1) return `Password is managed by ${labels[0]}.`;
    return `Password is managed by ${labels.join(" / ")}.`;
  }, [oauthProviders]);

  const [editing, setEditing] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [firstNameError, setFirstNameError] = useState<string | undefined>();
  const [changingPassword, setChangingPassword] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isPending && !user) {
      router.push("/login");
    }
  }, [user, isPending, router]);

  useEffect(() => {
    if (!user || editing) return;
    setFirstName(user.first_name ?? "");
    setLastName(user.last_name ?? "");
    setCompany(user.company ?? "");
    setRole(user.jobtitle ?? "");
  }, [user, editing]);

  const handleReconcile = () => {
    reconcile(
      {},
      {
        onSuccess: () => {
          toast.success("Billing reconciled", {
            description: "Your billing setup has been synced with Stripe.",
          });
        },
        onError: (err) => {
          toast.error("Reconciliation failed", {
            description: err instanceof Error ? err.message : "Please try again or contact support.",
          });
        },
      },
    );
  };

  const startEditing = () => {
    if (!user) return;
    setFirstName(user.first_name ?? "");
    setLastName(user.last_name ?? "");
    setCompany(user.company ?? "");
    setRole(user.jobtitle ?? "");
    setFirstNameError(undefined);
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setFirstNameError(undefined);
  };

  const handleSave = async () => {
    if (!user) return;

    const existingJobtitle = normalizeOptionalString(user.jobtitle);
    const nextJobtitle = normalizeOptionalString(role);

    const nextFirst = firstName.trim();
    const nextLast = normalizeOptionalString(lastName);
    const nextCompany = normalizeOptionalString(company);

    const prevFirst = (user.first_name ?? "").trim();
    const prevLast = normalizeOptionalString(user.last_name);
    const prevCompany = normalizeOptionalString(user.company);
    const prevJobtitle = existingJobtitle;

    const fields: ProfileStringField[] = [];
    const variables: Record<string, string | null> & { id: string } = { id: user.id };

    // Only send WorkOS-backed name fields when dirty so company/jobtitle-only saves stay local.
    // Blank first_name is allowed when unchanged (API nullable; display falls back to name).
    if (nextFirst !== prevFirst) {
      if (!nextFirst) {
        setFirstNameError("First name is required.");
        return;
      }
      fields.push("first_name");
      variables.first_name = nextFirst;
    }
    if (nextLast !== prevLast) {
      fields.push("last_name");
      variables.last_name = nextLast;
    }
    if (nextJobtitle !== prevJobtitle) {
      fields.push("jobtitle");
      variables.jobtitle = nextJobtitle;
    }
    if (nextCompany !== prevCompany) {
      fields.push("company");
      variables.company = nextCompany;
    }

    setFirstNameError(undefined);

    if (fields.length === 0) {
      setEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await graphqlRequest<UpdateProfileResult, Record<string, unknown>>(buildUpdateProfileDocument(fields), variables);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
      toast.success("Profile updated");
      setEditing(false);
    } catch (err) {
      if (err instanceof ClientError && err.response.status === 401) {
        if (!shouldSkipErrorToast(err)) {
          toast.error("Your session has expired. Sign in again to continue.");
        }
        return;
      }
      if (shouldSkipErrorToast(err)) {
        return;
      }
      toast.error(graphqlErrorMessage(err, "Something went wrong. Please try again."));
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!canChangePassword) return;
    setChangingPassword(true);
    try {
      const message = await requestChangePasswordEmail();
      toast.success(message);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to start password change.");
    } finally {
      setChangingPassword(false);
    }
  };

  if (isPending && !user) {
    return <div className="flex min-h-screen items-center justify-center">Loading...</div>;
  }

  if (!user) {
    return null;
  }

  const nameValue = displayName(user.first_name, user.last_name, user.name);
  const companyDisplay = user.company?.trim() ? user.company : "";
  const roleDisplay = user.jobtitle?.trim() ? user.jobtitle : "";

  const changePasswordButton = (
    <Button
      variant="outline"
      size="sm"
      onClick={() => void handleChangePassword()}
      disabled={!canChangePassword || changingPassword}
    >
      {changingPassword ? "Sending…" : "Change password"}
    </Button>
  );

  return (
    <div className="@container/main flex flex-1 flex-col gap-2">
      <div className="flex-1 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Account</h1>
          <p className="text-muted-foreground">Manage your profile, security settings, and account preferences.</p>
        </div>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <IconUser className="size-5" aria-hidden />
                Profile
              </CardTitle>
              <CardDescription>Your account details.</CardDescription>
            </div>
            {editing ? (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={cancelEditing} disabled={isSaving}>
                  Cancel
                </Button>
                <Button size="sm" onClick={() => void handleSave()} disabled={isSaving}>
                  {isSaving ? "Saving…" : "Save"}
                </Button>
              </div>
            ) : (
              <Button variant="outline" size="sm" onClick={startEditing}>
                Edit profile
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
              <Avatar className="size-20 shrink-0" name={nameValue} email={user.email} src={user.avatarUrl} />
              <div className="grid flex-1 gap-4 sm:grid-cols-2">
                {editing ? (
                  // Occupy the same grid cell as view-mode "Name" so Email/Company/Role stay put.
                  // Nested first/last sit side-by-side from sm up; stack on mobile with the rest.
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="account-first-name">First name</Label>
                      <Input
                        id="account-first-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        aria-invalid={Boolean(firstNameError)}
                        aria-describedby={firstNameError ? "account-first-name-error" : undefined}
                      />
                      {firstNameError ? (
                        <p id="account-first-name-error" className="text-sm text-destructive">
                          {firstNameError}
                        </p>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="account-last-name">Last name</Label>
                      <Input id="account-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="account-name">Name</Label>
                    <Input
                      id="account-name"
                      readOnly
                      tabIndex={-1}
                      value={nameValue}
                      className={readOnlyFieldClassName}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="account-email">Email</Label>
                  <Input
                    id="account-email"
                    readOnly
                    tabIndex={-1}
                    value={user.email}
                    className={readOnlyFieldClassName}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account-company">Company</Label>
                  {editing ? (
                    <Input
                      id="account-company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      placeholder="Organization name"
                    />
                  ) : (
                    <Input
                      id="account-company"
                      readOnly
                      tabIndex={-1}
                      value={companyDisplay}
                      placeholder="Not set"
                      className={readOnlyFieldClassName}
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="account-role">Role</Label>
                  {editing ? (
                    <Select
                      value={role || null}
                      onValueChange={(value) => {
                        if (typeof value === "string") {
                          setRole(value);
                        }
                      }}
                    >
                      <SelectTrigger id="account-role" className="w-full">
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                      <SelectContent>
                        {role && !isOnboardingRole(role) ? <SelectItem value={role}>{role}</SelectItem> : null}
                        {ONBOARDING_ROLES.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id="account-role"
                      readOnly
                      tabIndex={-1}
                      value={roleDisplay}
                      placeholder="Not set"
                      className={readOnlyFieldClassName}
                    />
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconCreditCard className="size-5" aria-hidden />
              Billing
            </CardTitle>
            <CardDescription>
              Manage payment methods, invoices, and billing details in the billing portal.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Button asChild>
                <Link href="/manage/billing">Manage billing</Link>
              </Button>
              {needsReconciliation ? (
                <Button variant="outline" onClick={handleReconcile} disabled={isReconciling}>
                  <IconRefresh className="mr-2 size-4" aria-hidden />
                  {isReconciling ? "Reconciling…" : "Reconcile billing"}
                </Button>
              ) : null}
            </div>
            {needsReconciliation ? (
              <p className="text-sm text-muted-foreground">
                Your billing setup appears out of sync. Use &ldquo;Reconcile billing&rdquo; to re-sync your payment
                method and WorkOS account data.
              </p>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <IconShield className="size-5" aria-hidden />
              Security
            </CardTitle>
            <CardDescription>Manage how you sign in to Radium.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium">Password</p>
                <p className="text-sm text-muted-foreground">We&apos;ll email you a link to set a new password.</p>
              </div>
              {canChangePassword ? (
                changePasswordButton
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex">{changePasswordButton}</span>
                  </TooltipTrigger>
                  <TooltipContent>{oauthPasswordTooltip}</TooltipContent>
                </Tooltip>
              )}
            </div>
            <Separator />
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium">Two-factor authentication</p>
                <p className="text-sm text-muted-foreground">
                  Two-factor authentication is managed by your identity provider.
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Badge variant="secondary">Managed</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">Danger zone</CardTitle>
            <CardDescription>
              Permanently delete your account and all associated data. This action cannot be undone. All API keys will
              be revoked and your usage history will be permanently removed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">
                  <Button variant="destructive" disabled>
                    Delete account
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>Contact support@radium.cloud</TooltipContent>
            </Tooltip>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
